/**
 * Shared formatters + tiny helpers for the structured PR2 renderers.
 *
 * These exist because every renderer needs the same currency / date /
 * number formatting and pulling them into one place keeps visual output
 * uniform across the source pane.
 */

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const INT = new Intl.NumberFormat('en-US')

const DEC = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
})

export function fmtMoney(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return CURRENCY.format(n)
}

export function fmtInt(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return INT.format(n)
}

export function fmtNum(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return DEC.format(n)
}

/** ISO date / datetime → short locale string. Returns the input if unparseable. */
export function fmtDate(s: unknown): string {
  if (typeof s !== 'string' || !s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  // Drop the time component if input looks like a bare date
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Truthy/falsey to a small badge label. */
export function bool(b: unknown): string {
  return b ? 'Yes' : 'No'
}

/** Wrap children in a labelled cell — used heavily in invoice headers. */
export interface FieldProps {
  label: string
  value: React.ReactNode
  align?: 'left' | 'right'
}
